/**
 * Сравнение вариантов производственной программы и решение по бюджету (UC-5).
 *
 * Вайрфрейм (одобрен founder 26.08):
 *   `docs/superpowers/prototypes/season-planning-2027/06-compare-approve.html`
 *
 * Что экран обещает руководителю: варианты стоят колонками и посчитаны ПО
 * ОДНИМ ПРАВИЛАМ — себестоимость, выручка, маржа, урожайность, снимок
 * ТМЦ-потребности, выполнимость (или честное «не проверялась») и светофор
 * целей бюджета. Утверждение делает вариант рабочим бюджетом и
 * производственной программой сезона.
 *
 * Числа НЕ считаются на фронте. Всё, что видно в колонке, приходит с бэка тем
 * же расчётом, что кормит сводку одного варианта: пересчитать здесь хотя бы
 * маржу значило бы завести вторую правду о бюджете — и первый же спор о цифрах
 * выиграл бы Excel. Тут только группировка, подписи и форматирование.
 *
 * Язык — руководителя и плановика: «вариант программы», «рабочий бюджет»,
 * «вернуть на доработку». Модельные слова (scenario, canon, snapshot reason)
 * наружу не отдаём.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { describeApiError } from '@/lib/apiError';
import type { VariantTargets } from '@/lib/planning/budgetTargets';
import type { Coverage, VariantTotals } from '@/lib/planning/planVariant';
import {
  planVariantKeys,
  programCompareKeys,
  sppOverviewKeys,
  variantListKeys,
} from '@/lib/planning/queryKeys';
import { toast } from '@/lib/toast';

// ── Типы ответа ──────────────────────────────────────────────────────────────

export interface CompareCrop {
  crop_id: string | null;
  crop_name: string;
  area_ha: number;
  /** Доля от ПАШНИ хозяйства — пар и нераспределённое тоже занимают место. */
  share_pct: number | null;
  /** Средневзвешенная плановая урожайность, ц/га. Канон — план. */
  yield_c_ha: number | null;
  gross_t: number | null;
}

export interface CompareSnapshot {
  id: string;
  version_no: number;
  /** manual | recalculate | approve — чем документ был выпущен. */
  reason: string;
  basis: string;
  created_at: string;
  /** `null` — либо цен нет, либо деньги скрыты правами. Не ноль. */
  total_amount: number | null;
  items_count: number;
}

/** `not_checked` — ВАЛИДНОЕ состояние, а не ошибка: парк мог быть не заведён. */
export type FeasibilityStatus = 'not_checked' | 'ok' | 'risks' | 'blocked';

export interface AcceptedRisk {
  window_label: string;
  what: string;
  comment: string | null;
  accepted_by: string | null;
  accepted_at: string | null;
  is_accepted: boolean;
}

export interface CompareFeasibility {
  status: FeasibilityStatus;
  label: string;
  note: string | null;
  bottlenecks: number;
  accepted_risks: AcceptedRisk[];
}

export interface CompareVariant {
  scenario: {
    id: string;
    name: string;
    status: string;
    is_selected: boolean;
    farm_id: string;
    farm_name: string;
    season_id: string;
    season_year: number;
  };
  is_approved: boolean;
  crops: CompareCrop[];
  coverage: Coverage;
  totals: VariantTotals;
  targets: VariantTargets | null;
  snapshot: CompareSnapshot | null;
  feasibility: CompareFeasibility;
  can_approve: boolean;
  approve_blockers: string[];
}

export interface ApprovedState {
  scenario_id: string | null;
  scenario_name: string | null;
  plan_status: string;
  revision_no: number;
  approved_at: string | null;
  approved_by_user_id: string | null;
  snapshot_version_no: number | null;
}

export type ReturnAddressee = 'sowing_plan' | 'tech_cards';

export interface ProgramDecision {
  id: string;
  scenario_id: string;
  scenario_name: string | null;
  action: 'approve' | 'return';
  addressee: ReturnAddressee | null;
  comment: string | null;
  revision_no: number;
  snapshot_version_no: number | null;
  coverage_pct: number | null;
  created_by_user_id: string | null;
  created_at: string;
}

export interface ProgramCompare {
  farm_id: string;
  farm_name: string;
  season_id: string;
  season_year: number;
  prices_visible: boolean;
  approved: ApprovedState;
  variants: CompareVariant[];
  decisions: ProgramDecision[];
  truncated: boolean;
}

// ── Чтение ───────────────────────────────────────────────────────────────────

export function useProgramCompare(farmId: string | null, seasonId: string | null) {
  return useQuery<ProgramCompare, Error>({
    queryKey: programCompareKeys.compare(farmId, seasonId),
    queryFn: () =>
      api.get<ProgramCompare>(
        `/api/v1/planning/scenarios/compare?farm_id=${farmId}&season_id=${seasonId}`,
      ),
    enabled: !!farmId && !!seasonId,
    staleTime: 15_000,
  });
}

// ── Решение ──────────────────────────────────────────────────────────────────

/**
 * Решение по бюджету трогает состояние ЧЕТЫРЁХ экранов сразу: сравнение,
 * сводка варианта, переключатели вариантов («план сезона») и обзор СПП.
 * Инвалидация одна на все — иначе кнопка сработала, а сосед показывает
 * прежний утверждённый вариант.
 */
function useInvalidateDecision() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: programCompareKeys.all() });
    void qc.invalidateQueries({ queryKey: planVariantKeys.all() });
    void qc.invalidateQueries({ queryKey: variantListKeys.all() });
    void qc.invalidateQueries({ queryKey: sppOverviewKeys.all() });
  };
}

export interface ApproveResult {
  approved: ApprovedState;
  decision: ProgramDecision;
  snapshot: CompareSnapshot;
}

export function useApproveProgram() {
  const invalidate = useInvalidateDecision();
  return useMutation<ApproveResult, Error, { scenarioId: string; comment?: string }>({
    mutationFn: ({ scenarioId, comment }) =>
      api.post<ApproveResult>(`/api/v1/planning/scenarios/${scenarioId}/approve`, {
        comment: comment?.trim() || null,
      }),
    onSuccess: (res) => {
      invalidate();
      toast.success(
        `Вариант утверждён рабочим бюджетом. Снимок ТМЦ-потребности версии ${res.snapshot.version_no} зафиксирован.`,
      );
    },
    onError: (err) => toast.error(describeApiError(err)),
  });
}

export interface ReturnResult {
  approved: ApprovedState;
  decision: ProgramDecision;
}

export function useReturnProgram() {
  const invalidate = useInvalidateDecision();
  return useMutation<
    ReturnResult,
    Error,
    { scenarioId: string; addressee: ReturnAddressee; comment: string }
  >({
    mutationFn: ({ scenarioId, addressee, comment }) =>
      api.post<ReturnResult>(`/api/v1/planning/scenarios/${scenarioId}/return`, {
        addressee,
        comment: comment.trim(),
      }),
    onSuccess: (res) => {
      invalidate();
      toast.success(
        res.decision.addressee === 'tech_cards'
          ? 'Вернули на доработку техкарт. История сравнения осталась целой.'
          : 'Вернули на доработку посевного плана. История сравнения осталась целой.',
      );
    },
    onError: (err) => toast.error(describeApiError(err)),
  });
}

// ── Производные для экрана ───────────────────────────────────────────────────

export const ADDRESSEE_TITLES: Record<ReturnAddressee, string> = {
  sowing_plan: 'Посевной план',
  tech_cards: 'Техкарты',
};

/** Подпись статуса плана сезона словами руководителя, а не модели. */
export const PLAN_STATUS_TITLES: Record<string, string> = {
  planning: 'планируется',
  planned: 'бюджет утверждён',
  revised: 'на доработке',
  completed: 'сезон завершён',
  archived: 'в архиве',
};

/**
 * «Лучший из сравниваемых» по показателю — бейдж «ниже всех» / «выше всех».
 *
 * Возвращает id вариантов-победителей, а не значения: подсветка привязана к
 * колонке. Если посчитан только один вариант, победителя НЕТ — «лучший из
 * одного» ничего не сообщает и только льстит единственной колонке.
 */
export function bestVariantIds(
  variants: CompareVariant[],
  pick: (v: CompareVariant) => number | null | undefined,
  betterWhen: 'higher' | 'lower',
): Set<string> {
  const scored = variants
    .map((v) => ({ id: v.scenario.id, value: pick(v) }))
    .filter((x): x is { id: string; value: number } => typeof x.value === 'number' && Number.isFinite(x.value));
  if (scored.length < 2) return new Set();
  const best = scored.reduce((acc, x) =>
    betterWhen === 'higher' ? (x.value > acc.value ? x : acc) : x.value < acc.value ? x : acc,
  );
  // Ничья — подсвечиваем всех, кто её разделил: один произвольный «лучший»
  // из двух равных читался бы как «этот чем-то лучше», хотя это не так.
  return new Set(scored.filter((x) => x.value === best.value).map((x) => x.id));
}

/** Строка «пшеница 46% · подсолнечник 22%» — подпись посевного плана в колонке. */
export function cropShareLine(crops: CompareCrop[], limit = 3): string {
  const named = crops.filter((c) => c.crop_id !== null && c.share_pct !== null);
  if (named.length === 0) return 'культуры не назначены';
  return named
    .slice(0, limit)
    .map((c) => `${c.crop_name.toLowerCase()} ${Math.round(c.share_pct as number)}%`)
    .join(' · ');
}

/** «пшеница 45 · подсолнечник 25» — строка урожайностей (ц/га, канон — план). */
export function cropYieldLine(crops: CompareCrop[], limit = 4): string {
  const named = crops.filter((c) => c.crop_id !== null && c.yield_c_ha !== null);
  if (named.length === 0) return 'плановая урожайность не задана';
  return named
    .slice(0, limit)
    .map((c) => `${c.crop_name.toLowerCase()} ${Math.round(c.yield_c_ha as number)}`)
    .join(' · ');
}

/** «214 / 230 га» — покрытие полей техкартами в колонке. */
export function coverageLine(coverage: Coverage): string {
  const fmt = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });
  return `${fmt.format(Math.round(coverage.assigned_ha))} / ${fmt.format(Math.round(coverage.total_ha))} га`;
}

/**
 * Последствия утверждения — тем же списком, что на кнопке в макете.
 *
 * Вынесено в чистую функцию не ради красоты: текст «что именно произойдёт»
 * обязан совпадать с тем, что делает бэк, и проверяться тестом. Кнопка
 * «Утвердить», после которой человек узнаёт последствия постфактум, — это то,
 * из-за чего бюджеты и утверждают в почте.
 */
export function approveConsequences(variant: CompareVariant, hadApproved: boolean): string[] {
  const out = [
    'Вариант станет рабочим бюджетом и производственной программой сезона.',
  ];
  if (variant.snapshot) {
    out.push(
      `Снимок ТМЦ-потребности зафиксируется как рабочий (сейчас у варианта версия ${variant.snapshot.version_no}, утверждение выпустит новую).`,
    );
  } else {
    out.push('Снимок ТМЦ-потребности будет выпущен и зафиксирован как рабочий.');
  }
  if (hadApproved) {
    out.push('Прежний утверждённый вариант вернётся в черновики и останется в архиве сравнения.');
  } else {
    out.push('Остальные варианты останутся в архиве сравнения.');
  }
  out.push('Дальнейшие правки — только через новую версию: возврат на доработку, потом повторное утверждение.');
  if (variant.feasibility.status === 'not_checked') {
    out.push('Выполнимость по парку техники не проверялась — пометка поедет вместе с вариантом.');
  }
  return out;
}
